import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  BulkUpdateDocumentDto,
  CreateDocumentDto,
  DocumentDto,
  DocumentListDto,
  DocumentUploadStage,
  GenerateUploadUrlResponseDto,
  SelectListDto,
  UpdateDocumentDto,
  UploadDocumentDto,
  updateInListCache,
  DocumentColumnMaxValuesDto,
  touchesDocumentNumberRangeFields,
} from '@sib/api';
import { QueryKey } from '@sib/api/enums';
import {
  InferSearchParamsQueryTypeWithArraySorter,
  PaginationOptionsWithSortArray,
} from '@sib/api/types';

export type DocumentsKeys = keyof DocumentDto;

export const DOCUMENT_RESOURCE_NAME = 'document';

export const DocumentResourceField = {
  ID: 'id',
  STAGE: 'stage',
  VENDOR: 'vendor',
  FILE_URL: 'fileUrl',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  NAME: 'name',
  PROVIDED_BY: 'providedBy',
  SPEND_CATEGORY: 'spendCategory',
} as const satisfies Record<string, DocumentsKeys>;

const CACHE_TIME = 5000;

export interface DocumentsListQuery extends PaginationOptionsWithSortArray {
  searchTerm?: string;
  isContractActive?: boolean;
  originFilename?: string;
  excludeType?: string;
  withSubsidiaries?: boolean;
}

const getUpdatedFields = (variables: UpdateDocumentDto) =>
  new Set(
    Object.entries(variables)
      .filter(([, value]) => value !== undefined)
      .map(([key]) => key)
  );

const getFilterFieldsFromDocumentsQueryKey = (
  queryKey: readonly unknown[]
): Set<string> => {
  if (queryKey[0] !== QueryKey.Resource.Documents) return new Set();

  const queryParams = queryKey[1];
  if (
    !queryParams ||
    typeof queryParams !== 'object' ||
    Array.isArray(queryParams)
  )
    return new Set();

  const rawFilter = (queryParams as { filter?: unknown }).filter;
  if (!Array.isArray(rawFilter)) return new Set();

  return new Set(
    rawFilter
      .filter((entry): entry is string => typeof entry === 'string')
      .map(entry => entry.split(':', 1)[0])
      .filter(Boolean)
  );
};

const hasAnySharedField = (
  updatedDocumentFields: Set<string>,
  queryFilterFields: Set<string>
) => [...updatedDocumentFields].some(field => queryFilterFields.has(field));

export const useDocumentsList = (
  organizationId: string,
  query: DocumentsListQuery,
  options?: UseQueryOptions<DocumentListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && (options?.enabled ?? true),
    queryKey: [
      QueryKey.Resource.Documents,
      query,
      organizationId,
      ...(options?.queryKey || []),
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetAllDocuments(
        organizationId || '',
        query
      ),
  });

interface UseDocumentParams {
  documentId?: string;
  organizationId?: string;
}

export const useDocument = (
  { documentId, organizationId }: UseDocumentParams = {},
  options?: UseQueryOptions<DocumentDto>
) =>
  useQuery({
    ...options,
    enabled: !!documentId && !!organizationId,
    queryKey: [QueryKey.Resource.Documents, documentId, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetOneDocument(
        documentId || '',
        organizationId || ''
      ),
  });

export type UseUploadDocumentOptions = UseMutationOptions<
  DocumentDto,
  Error,
  UploadDocumentDto
> & {
  organizationId: string;
};

export const useUploadDocument = ({
  organizationId,
  ...options
}: UseUploadDocumentOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Documents],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async data =>
      api.organization.documentControllerUploadFile(organizationId, data),
  });
};

export interface DeleteDocumentPayload {
  organizationId: string;
  documentId: string;
}

export const useDocumentDelete = (
  options?: UseMutationOptions<unknown, Error, DeleteDocumentPayload>
) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Documents],
      });
      options?.onSuccess?.(...args);
    },
    mutationFn: async ({ documentId, organizationId }) =>
      api.organization.documentControllerDeleteDocuement(
        documentId,
        organizationId
      ),
  });
};

export type UseUpdatedDocumentOptions = UseMutationOptions<
  DocumentDto,
  Error,
  UpdateDocumentDto
> & {
  organizationId: string;
  documentId: string;
  updateDataOnSuccess?: boolean;
};

export const useUpdateDocument = ({
  organizationId,
  documentId,
  updateDataOnSuccess = false,
  ...options
}: UseUpdatedDocumentOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,

    mutationFn: async data =>
      api.organization.documentControllerUpdate(
        documentId,
        organizationId,
        data
      ),
    onSuccess: async (documentData, variables, context) => {
      if (variables.vendor !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentVendorSelectList],
        });
      }
      if (
        touchesDocumentNumberRangeFields(queryClient, organizationId, variables)
      )
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentColumnMaxValues, organizationId],
        });

      if (updateDataOnSuccess)
        queryClient.setQueriesData<DocumentListDto>(
          { queryKey: [QueryKey.Resource.Documents] },
          old =>
            updateInListCache({
              old,
              id: documentId,
              updater: item => ({
                ...item,
                ...documentData,
              }),
            })
        );

      const updatedFields = getUpdatedFields(variables);

      if (updatedFields.size > 0)
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Documents],
          refetchType: 'none',
        });

      if (updatedFields.size > 0)
        queryClient.invalidateQueries({
          predicate: query => {
            const filterFields = getFilterFieldsFromDocumentsQueryKey(
              query.queryKey
            );

            return (
              filterFields.size > 0 &&
              hasAnySharedField(updatedFields, filterFields)
            );
          },
          refetchType: 'active',
        });

      options.onSuccess?.(documentData, variables, context);
    },
  });
};

interface UseCreateDocumentUploadUrlProps {
  organizationId: string;
}

export const useCreateDocumentUploadUrl = (
  { organizationId }: UseCreateDocumentUploadUrlProps,
  options?: UseMutationOptions<
    GenerateUploadUrlResponseDto,
    Error,
    CreateDocumentDto
  >
): UseMutationResult<GenerateUploadUrlResponseDto, Error, CreateDocumentDto> =>
  useMutation({
    ...options,
    mutationFn: (document: CreateDocumentDto) =>
      api.organization.documentControllerGetUploadUrl(organizationId, document),
  });

interface UseGetDocumentUploadStatusParams {
  batchId: string;
  organizationId: string;
}

export const useGetDocumentUploadStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: data => {
      const areAllFilesProcessed =
        data.items.length > 0 &&
        data.items.every(item =>
          [DocumentUploadStage.Failed, DocumentUploadStage.Uploaded].includes(
            item.status
          )
        );

      if (areAllFilesProcessed) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.Documents],
        });
      }
    },
    mutationFn: ({
      organizationId,
      batchId,
    }: UseGetDocumentUploadStatusParams) =>
      api.organization.documentControllerGetUploadStatus(
        organizationId,
        batchId
      ),
  });
};

export const useDocumentTypeSelectList = (
  organizationId: string,
  options?: UseQueryOptions<SelectListDto> & {
    filter?: string[];
    sort?: string[];
  }
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.DocumentTypeSelectList,
      organizationId,
      options?.filter,
      options?.sort,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetDocumentTypeSelectList(
        organizationId || '',
        {
          filter: options?.filter,
          sort: options?.sort,
        }
      ),
  });

export const useDocumentSpendCategorySelectList = (
  organizationId: string,
  query: InferSearchParamsQueryTypeWithArraySorter<
    typeof api.organization.documentControllerGetSpendCategorySelectList,
    1
  >,
  options?: UseQueryOptions<SelectListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.DocumentSpendCategorySelectList,
      organizationId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetSpendCategorySelectList(
        organizationId || '',
        query
      ),
  });

export const useDocumentVendorSelectList = (
  organizationId: string,
  options?: Omit<UseQueryOptions<SelectListDto>, 'queryKey'> & {
    filter?: string[];
    sort?: string[];
  }
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.DocumentVendorSelectList,
      organizationId,
      options?.filter,
      options?.sort,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetVendorSelectList(
        organizationId || '',
        {
          filter: options?.filter,
          sort: options?.sort,
        }
      ),
  });

export type UseBulkUpdateDocumentsOptions = UseMutationOptions<
  DocumentDto[],
  Error,
  BulkUpdateDocumentDto
> & {
  organizationId: string;
};

export const useBulkUpdateDocuments = ({
  organizationId,
  ...options
}: UseBulkUpdateDocumentsOptions) => {
  const queryClient = useQueryClient();

  return useMutation({
    ...options,
    mutationFn: async (data: BulkUpdateDocumentDto) =>
      api.organization.documentControllerBulkUpdate(organizationId, data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.Resource.Documents],
      });
      if (
        touchesDocumentNumberRangeFields(queryClient, organizationId, variables)
      )
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentColumnMaxValues, organizationId],
        });

      if (variables.vendor !== undefined) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.Resource.DocumentVendorSelectList],
        });
      }

      options?.onSuccess?.(data, variables, context);
    },
  });
};

export const useDocumentColumnMaxValues = (
  organizationId: string,
  options?: UseQueryOptions<DocumentColumnMaxValuesDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.DocumentColumnMaxValues, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organization.documentControllerGetColumnMaxValues(
        organizationId || ''
      ),
  });

export type DocumentCsvExportQuery = Parameters<
  typeof api.organization.documentControllerExportDocuments
>[1];

export type UseExportDocumentsCsvOptions = UseMutationOptions<
  Blob,
  Error,
  DocumentCsvExportQuery
> & {
  organizationId: string;
};

export const useExportDocumentsCsv = ({
  organizationId,
  ...options
}: UseExportDocumentsCsvOptions) =>
  useMutation({
    ...options,
    mutationFn: async query =>
      api.organization.documentControllerExportDocuments(
        organizationId,
        query,
        { format: 'blob' }
      ) as Promise<Blob>,
  });
